import cx from 'classnames';
import React, { type ChangeEvent, type FC, useEffect, useState } from 'react';
import styles from './styles.module.css';

export interface InputRangeProps {
  fromPlaceholder?: string;
  toPlaceholder?: string;
  fromKey: string; // Сделаем обязательными, так как они критичны
  toKey: string; // Сделаем обязательными
  filterName: string;
  onChangeFrom: (key: string, value: string) => void;
  onChangeTo: (key: string, value: string) => void;
  initialValues?: [
    string | number | null | undefined,
    string | number | null | undefined,
  ];
  unit?: string;
  size?: number;
  applyOnBlur?: boolean; // если true — применять только после onBlur
}

// Форматируем число с пробелами в качестве разделителей тысяч
const formatWithSpaces = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  // добавляем пробелы для групп по 3 цифры
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

// Оставляем только цифры (для передачи в onChange и в параметры URL)
const toDigits = (value: string): string => value.replace(/\D/g, '');

const InputRange: FC<InputRangeProps> = ({
  fromPlaceholder,
  toPlaceholder,
  onChangeFrom,
  onChangeTo,
  initialValues = ['', ''],
  unit = '',
  fromKey,
  toKey,
  size,
  applyOnBlur = false,
}) => {
  const [fromValue, setFromValue] = useState<string>(
    initialValues[0] ? formatWithSpaces(String(initialValues[0])) : '',
  );
  const [toValue, setToValue] = useState<string>(
    initialValues[1] ? formatWithSpaces(String(initialValues[1])) : '',
  );

  // Синхронизация внутренних значений при изменении initialValues извне (например, при смене URL)
  useEffect(() => {
    const [initFrom, initTo] = initialValues;
    setFromValue(initFrom ? formatWithSpaces(String(initFrom)) : '');
    setToValue(initTo ? formatWithSpaces(String(initTo)) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues?.[0], initialValues?.[1]]);

  const handleFromChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const formatted = formatWithSpaces(raw);
    setFromValue(formatted);
    if (!applyOnBlur) {
      onChangeFrom(fromKey, toDigits(formatted));
    }
  };

  const handleToChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const formatted = formatWithSpaces(raw);
    setToValue(formatted);
    if (!applyOnBlur) {
      onChangeTo(toKey, toDigits(formatted));
    }
  };

  const handleFromBlur = () => {
    if (applyOnBlur) {
      onChangeFrom(fromKey, toDigits(fromValue));
    }
  };

  const handleToBlur = () => {
    if (applyOnBlur) {
      onChangeTo(toKey, toDigits(toValue));
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.inputContainer}>
        <div className={styles.inputWrapper}>
          <input
            type="text"
            inputMode="numeric"
            className={cx(styles.input, styles.fromInput)}
            placeholder={`${fromPlaceholder}${unit ? ' ' + unit : ''}`}
            value={fromValue}
            onChange={handleFromChange}
            onBlur={handleFromBlur}
            style={size ? { width: size } : {}}
          />
        </div>
        <div className={styles.inputWrapper}>
          <input
            type="text"
            inputMode="numeric"
            className={cx(styles.input, styles.toInput)}
            placeholder={`${toPlaceholder}${unit ? ' ' + unit : ''}`}
            value={toValue}
            onChange={handleToChange}
            onBlur={handleToBlur}
            style={size ? { width: size } : {}}
          />
        </div>
      </div>
    </div>
  );
};

export default InputRange;
